import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TablePagination,
  Collapse,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import pxToRem from "../../../../../Themes/Functions/pxToRem";
import uuid from "react-uuid";
import { noScrollBarStyle } from "../../../../../Configurations/AppStyle";
import moment from "moment";
import AppService from "../../../../../services/AppService";
import { useMockData } from "../../../../../contexts/MockDataContext";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// Helper functions
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};


const TicketMapping = ({ mappings = [], walletBalance = 1000, bankBalance = 0, onBalanceUpdate, selectedOrgId = "", showChannelPartnerCommission = false, hideFilters = false, hidePagination = false }) => {
  const { isMockDataEnabled } = useMockData();
  const [ticketData, setTicketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  
  const [selected, setSelected] = useState([]);
  const [totalSelected, setTotalSelected] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [exceedsBalance, setExceedsBalance] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  // ROOT_ADMIN partner view state
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginatedData, setPaginatedData] = useState([]);
  const [processSuccess, setProcessSuccess] = useState(false);
  // Partner balance when ROOT_ADMIN selects a partner
  const [partnerBalance, setPartnerBalance] = useState(null);
  const [partnerBalanceLoading, setPartnerBalanceLoading] = useState(false);
  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'ONLINE' or 'OFFLINE'
  const [offlinePaymentType, setOfflinePaymentType] = useState('BANK_TRANSFER'); // 'BANK_TRANSFER' or 'CHEQUE'
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [partnerBankAccounts, setPartnerBankAccounts] = useState([]);
  const [selectedPartnerBank, setSelectedPartnerBank] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null); // 'processing', 'success', 'error', null
  const [processingMessage, setProcessingMessage] = useState('');
  // Payment detail dialog state
  const [showPaymentDetailDialog, setShowPaymentDetailDialog] = useState(false);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);

    // Get principal info from localStorage - this is the proper way to check user roles      
  // Try to get principalInfo from authInfo which contains role information
  const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
  const principalInfo = authInfo.principalInfo || {};
  console.log('Principal info for admin check:', principalInfo);
  
  // Check for admin roles in principalInfo
  // The roles array contains strings like "Root Admin" instead of "ROLE_ROOT_ADMIN"
  
  const organisationId = principalInfo.organisationId || principalInfo.OrganisationId;
  console.log('Organization ID:', organisationId); 
  // Derive ROOT_ADMIN flag from roles (handle variations like 'Root Admin', 'ROLE_ROOT_ADMIN')
  const roles = principalInfo.roles || [];
  const isRootAdmin = Array.isArray(roles) && roles.some((r) => {
    const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
    const s = String(val);
    return /(root\s*admin|role_root_admin|root_admin)/i.test(s) || (s.toLowerCase().includes('root') && s.toLowerCase().includes('admin'));
  });
  // Detect Partner users (e.g., Partner Admin/Users) to restrict processing UI
  const isPartnerUser = Array.isArray(roles) && roles.some((r) => {
    const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
    const s = String(val).toLowerCase();
    return s.includes('partner') && !s.includes('root');
  });

  // Load partner organisations for ROOT_ADMIN when no external selection is provided
  useEffect(() => {
    if (!isRootAdmin) return;
    if (selectedOrgId) return; // container controls selection; no need to load list
    AppService.get("v1/organisation/keyValues?type=PARTNER")
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        setPartners(list);
      })
      .catch(() => setPartners([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRootAdmin, selectedOrgId]);

  // Helper function to normalize ticket data and calculate missing financial fields
  const normalizeTicketData = (tickets) => {
    return tickets.map(ticket => {
      // Normalize field names from TicketResponse to match component expectations
      const normalizedTicket = {
        ...ticket,
        // Map TicketResponse fields to component field names
        ticketId: ticket.id || ticket.ticketId,
        ticketNumber: ticket.ticketNumber || ticket.orgTicketNumber,
        createdDate: ticket.createdAt ? new Date(ticket.createdAt * 1000).toISOString() : ticket.createdDate,
        description: ticket.serviceName || ticket.description,
        // Set default values for missing fields
        paymentStatus: ticket.paymentStatus || 'UNPAID',
        // For service partner payments, use partnerPayment (govt fees + 60% service charge)
        // This is the amount to be paid to the service partner
        totalCost: ticket.partnerPayment || ticket.totalCost || ticket.fine || 0
      };

      // If the API already provides all financial fields, use them
      if (normalizedTicket.partnerServiceFee !== undefined && normalizedTicket.partnerServiceFee !== null) {
        return normalizedTicket;
      }

      // Otherwise, calculate based on the distribution model:
      // Partner Service Fee: 60% of service charge
      // Channel Partner Commission: 12% of service charge
      // Thaklis Profit: 28% of service charge
      const serviceCharge = normalizedTicket.serviceCharge || 0;
      const governmentFees = normalizedTicket.governmentFees || 0;

      const partnerServiceFee = serviceCharge * 0.60;
      const channelPartnerCommission = serviceCharge * 0.12;
      const expectedThaklisFee = serviceCharge * 0.28;
      const partnerPayment = governmentFees + partnerServiceFee;

      return {
        ...normalizedTicket,
        partnerServiceFee,
        partnerPayment,
        channelPartnerCommission,
        expectedThaklisFee
      };
    });
  };

  // Function to process ticket data from parent (via mappings prop)
  const fetchTicketData = () => {
    // If mock data is enabled or mappings are provided from parent, use those directly
    if (isMockDataEnabled || (Array.isArray(mappings) && mappings.length > 0)) {
      console.log("[TicketMapping] Using mappings from parent component:", mappings);
      setTicketData(normalizeTicketData(mappings || []));
      setLoading(false);
      setError(null);
      return;
    }

    // Only call API if no mappings provided from parent
    console.log("[TicketMapping] No mappings from parent, clearing ticket data");
    setTicketData([]);
    setLoading(false);
    setError(null);
  };

  // Fetch partner wallet balance for ROOT_ADMIN when a target partner org is set
  useEffect(() => {
    if (!isRootAdmin) {
      setPartnerBalance(null);
      return;
    }
    const targetOrgId = selectedOrgId || selectedPartnerId;
    if (!targetOrgId) return;
    setPartnerBalanceLoading(true);
    AppService.get(`/v1/wallet/transactions/balance/${targetOrgId}`)
      .then((resp) => {
        const dto = resp?.data && (resp.data.balance !== undefined ? resp.data : resp);
        const bal = dto?.balance ?? resp?.balance;
        setPartnerBalance(Number(bal) || 0);
      })
      .catch((e) => {
        console.error('Failed to fetch partner balance', e);
        setPartnerBalance(null);
      })
      .finally(() => setPartnerBalanceLoading(false));
  }, [isRootAdmin, selectedOrgId, selectedPartnerId]);

  // Re-validate when partner balance loads/changes for ROOT_ADMIN
  useEffect(() => {
    if (!isRootAdmin) return;
    if (partnerBalance === null || partnerBalance === undefined) {
      setExceedsBalance(false);
    } else {
      setExceedsBalance(Number(totalSelected) > Number(partnerBalance));
    }
  }, [isRootAdmin, partnerBalance, totalSelected]);
  
  // Fetch transaction data on component mount and when mock data changes
  useEffect(() => {
    fetchTicketData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMockDataEnabled, mappings]);

  // Refetch when ROOT_ADMIN switches partner or when container selection changes
  useEffect(() => {
    if (isRootAdmin && !isMockDataEnabled) {
      fetchTicketData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId, selectedOrgId]);
  
  // Filter and search data when dependencies change
  useEffect(() => {
    // Log the raw data to see its structure
    console.log("Raw ticketData:", ticketData);

    // Start with all tickets
    let result = [...ticketData];
    console.log("result1 (all data):", result);

    // Filter by selected partner if ROOT_ADMIN and a partner is selected
    if (isRootAdmin && (selectedPartnerId || selectedOrgId)) {
      const targetOrgId = selectedOrgId || selectedPartnerId;
      result = result.filter(ticket => {
        // Check if ticket has partner organization matching the selected partner
        const ticketPartnerOrgId = ticket.partnerOrganisationId ||
                                    ticket.partnerOrganisation?.id ||
                                    ticket.partnerOrgId ||
                                    ticket.servicePartnerOrganisationId;
        return ticketPartnerOrgId === targetOrgId;
      });
      console.log(`Filtered by partner ${targetOrgId}:`, result);
    }

    // Apply status filter if not set to 'all'
    if (statusFilter !== 'all') {
      result = result.filter(ticket =>
        ticket.status && ticket.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    console.log("result2 (after status filter):", result);

    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(ticket =>
        (ticket.id && ticket.id.toString().toLowerCase().includes(searchLower)) ||
        (ticket.description && ticket.description.toString().toLowerCase().includes(searchLower)) ||
        (ticket.orgTicketNumber && ticket.orgTicketNumber.toString().toLowerCase().includes(searchLower)) ||
        (ticket.serviceName && ticket.serviceName.toString().toLowerCase().includes(searchLower))
      );
    }
    console.log("result3 (after search):", result);

    setFilteredData(result);

    // Reset selection when filters change
    setSelected([]);
    setTotalSelected(0);

    // Reset to first page when filters change
    setPage(0);
  }, [ticketData, searchTerm, statusFilter, selectedPartnerId, selectedOrgId, isRootAdmin]);
  
  // Handle pagination of filtered data
  useEffect(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  }, [filteredData, page, rowsPerPage]);

  // Handle checkbox selection
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Use ticketId consistently (same as table row selection)
      const newSelected = filteredData.map((ticket) => ticket.ticketId || ticket.id);
      setSelected(newSelected);
      calculateTotal(newSelected);
      return;
    }
    setSelected([]);
    setTotalSelected(0);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
    calculateTotal(newSelected);
  };

  // Handle expand/collapse of ticket rows
  const handleRowExpand = (ticketId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  // Calculate total amount of selected tickets
  const calculateTotal = (selectedIds) => {
    // Calculate total based on the appropriate amount field
    const amountField = showChannelPartnerCommission ? 'channelPartnerCommission' : 'partnerPayment';
    const total = filteredData
      // Use ticketId consistently (same as table row selection)
      .filter(ticket => selectedIds.includes(ticket.ticketId || ticket.id))
      .reduce((sum, ticket) => sum + (ticket[amountField] || 0), 0);
    setTotalSelected(total);
    // Check if selected total exceeds bank balance
    setInsufficientBalance(total > bankBalance);
    setExceedsBalance(false);
  };

  const isSelected = (id) => selected.includes(id);

  // Handle processing selected tickets - open payment dialog
  const handleProcessSelected = async () => {
    // Validate selection
    if (selected.length === 0) {
      setError("Please select at least one ticket to process");
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Load bank accounts and open payment dialog
    try {
      let accounts = [];

      // Fetch real bank accounts from admin API - only active operating accounts
      try {
        const [bankResponse, pendingResponse] = await Promise.all([
          AppService.get("/v1/admin/bank-accounts"),
          AppService.get("v2/partner-payment-transactions/pending").catch(() => [])
        ]);
        const allAccounts = Array.isArray(bankResponse) ? bankResponse : bankResponse?.data || [];
        const pendingTxns = Array.isArray(pendingResponse) ? pendingResponse : [];

        // Calculate pending committed amount per source bank account
        const pendingPerBank = {};
        pendingTxns.forEach(txn => {
          if (txn.sourceBankAccountId) {
            pendingPerBank[txn.sourceBankAccountId] = (pendingPerBank[txn.sourceBankAccountId] || 0) + Number(txn.amount || 0);
          }
        });

        // Any ACTIVE Thaklis bank can fund a service-partner payout —
        // gateway settlement accounts included. No type filter here.
        accounts = allAccounts
          .filter(acc => acc.status === 'ACTIVE')
          .map(acc => ({
            ...acc,
            pendingCommitted: pendingPerBank[acc.id] || 0,
            availableBalance: (acc.balance || 0) - (pendingPerBank[acc.id] || 0)
          }));
        console.log('Loaded bank accounts with available balance:', accounts);
      } catch (apiErr) {
        console.error('Failed to load bank accounts:', apiErr);
        setError("Failed to load bank accounts");
        return;
      }

      setBankAccounts(accounts);

      // Fetch partner bank accounts based on selected tickets
      try {
        // Get partner org ID from first selected ticket (use ticketId consistently)
        const selectedTicket = filteredData.find(t => selected.includes(t.ticketId || t.id));
        const partnerOrgId = selectedTicket?.servicePartnerOrganisationId || selectedTicket?.partnerOrganisationId || selectedPartnerId;

        if (partnerOrgId) {
          const partnerBanks = await AppService.get(`/v1/partner-bank-accounts/partner/${partnerOrgId}`);
          setPartnerBankAccounts(Array.isArray(partnerBanks) ? partnerBanks : []);
          console.log('Loaded partner bank accounts:', partnerBanks);
        } else {
          setPartnerBankAccounts([]);
        }
      } catch (apiErr) {
        console.warn('Failed to load partner bank accounts:', apiErr);
        setPartnerBankAccounts([]);
      }

      setShowPaymentDialog(true);
    } catch (err) {
      setError("Failed to load bank accounts");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle viewing payment detail
  const handleViewPaymentDetail = (ticket) => {
    setSelectedTicketDetail(ticket);
    setShowPaymentDetailDialog(true);
  };

  // Handle confirming payment after selecting payment method
  const handleConfirmPayment = async () => {
    // Validate payment method and bank account
    if (!paymentMethod) {
      setError("Please select a payment method");
      return;
    }
    if (!selectedBankAccount) {
      setError("Please select a bank account");
      return;
    }

    setProcessing(true);
    setError(null);
    setShowPaymentDialog(false);
    setProcessingStatus('processing');
    setProcessingMessage('Processing your payment...');

    try {
      // Get user and organization info
      const userId = principalInfo.id || principalInfo.userId;
      // For ROOT_ADMIN, require an explicit partner selection (container or local)
      const targetOrgId = selectedOrgId || (isRootAdmin ? (selectedPartnerId || null) : organisationId);

      // Validate user and organization info
      if (!userId || !targetOrgId) {
        setError(isRootAdmin ? "Please select a Partner Organization to process payment" : "User or organization information is missing");
        setProcessing(false);
        setProcessingStatus('error');
        setProcessingMessage(isRootAdmin ? "Please select a Partner Organization to process payment" : "User or organization information is missing");
        return;
      }

      // Prepare payload for the v2 partner payment API
      const paymentPayload = {
        partnerOrganisationId: targetOrgId,
        amount: totalSelected,
        paymentMethod: paymentMethod === 'OFFLINE' ? offlinePaymentType : paymentMethod, // BANK_TRANSFER, ONLINE, etc.
        partnerBankAccountId: selectedPartnerBank,
        sourceBankAccountId: selectedBankAccount, // Thaklis bank account from which payment is debited
        description: `Service partner payment for ${selected.length} ticket(s)`,
        ticketIds: selected
      };

      console.log("Sending partner payment request:", paymentPayload);

      // Call the v2 partner payment transactions API
      const response = await AppService.post("/v2/partner-payment-transactions", paymentPayload);

      // The response is already the data object we need
      console.log("Partner payment API response:", response);

      // Process the response - check for success or valid response
      if (response && (response.success !== false)) {
        // Update local data to reflect changes
        const updatedData = ticketData.map(ticket => {
          // Use ticketId consistently
          if (selected.includes(ticket.ticketId || ticket.id)) {
            return { ...ticket, paymentStatus: 'PAID' };
          }
          return ticket;
        });

        setTicketData(updatedData);

        // Update balances
        if (isRootAdmin) {
          // Refresh only partner balance from server; do not touch Thaklis account balance
          try {
            const balResp = await AppService.get(`/v1/wallet/transactions/balance/${targetOrgId}`);
            const dto = balResp?.data && (balResp.data.balance !== undefined ? balResp.data : balResp);
            const bal = dto?.balance ?? balResp?.balance;
            setPartnerBalance(Number(bal) || 0);
          } catch (e) {
            console.warn('Failed to refresh partner balance after payment', e);
          }
        } else if (onBalanceUpdate) {
          onBalanceUpdate(walletBalance - totalSelected);
        }

        // Reset selection and update UI
        setProcessSuccess(true);
        setSelected([]);
        setTotalSelected(0);
        setPaymentMethod(null);
        setSelectedBankAccount(null);

        // Show success message
        setError(null);
        setProcessingStatus('success');
        setProcessingMessage('Payment processed successfully!');

        // Reset success message after 3 seconds and refresh the ticket data
        setTimeout(() => {
          setProcessSuccess(false);
          setProcessingStatus(null);
          // Refresh the ticket data to update the table
          fetchTicketData();
        }, 3000);
      } else {
        // Handle API error response
        setError(response?.message || "Failed to process payment");
        setProcessingStatus('error');
        setProcessingMessage(response?.message || "Failed to process payment");
      }
    } catch (error) {
      console.error('Error processing tickets:', error);
      // Spring-style: response.data.message. Some legacy endpoints used
      // response.data.error. Fall back to plain-string body, then to a
      // generic message. Without this, server-side validation errors like
      // "Ticket X already has a pending or approved payment transaction"
      // were rendered as the generic fallback.
      const data = error?.response?.data;
      const errorMsg =
        (data && typeof data === 'object' && (data.message || data.error)) ||
        (typeof data === 'string' && data) ||
        error.message ||
        "Error processing payment";
      setError(errorMsg);
      setProcessingStatus('error');
      setProcessingMessage(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card sx={{ height: '100%', padding: pxToRem(20), display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Unpaid Tickets
          {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                mr: 2,
                fontWeight: 'bold',
                color: 'inherit'
              }}
            >
              Selected Total: {formatCurrency(totalSelected)}
            </Typography>
            {!isPartnerUser && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={
                  selected.length === 0 ||
                  processing ||
                  insufficientBalance ||
                  (isRootAdmin && (!selectedOrgId && !selectedPartnerId))
                }
                onClick={handleProcessSelected}
                startIcon={processing && <CircularProgress size={16} color="inherit" />}
              >
                {processing ? 'Processing...' : 'Process Selected'}
              </Button>
            )}
          </Box>
          {processSuccess && (
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold', mt: 1 }}>
              Successfully payment done for the tickets.
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Error message display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Insufficient balance warning */}
      {insufficientBalance && selected.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Insufficient Bank Balance</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              Selected total ({formatCurrency(totalSelected)}) exceeds available bank balance ({formatCurrency(bankBalance)}).
              Please reduce selection or add funds to bank account.
            </Typography>
          </Box>
        </Alert>
      )}
      
      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        {!hideFilters && isRootAdmin && !selectedOrgId && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: '240px' }} disabled={loading}>
            <InputLabel id="partner-filter-label">Partner Organization</InputLabel>
            <Select
              labelId="partner-filter-label"
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value)}
              label="Partner Organization"
            >
              <MenuItem value="" disabled>Select Partner</MenuItem>
              {Array.isArray(partners) && partners.length > 0 ? (
                partners.map((p) => (
                  <MenuItem key={(p.id ?? p.value) || p.key} value={(p.id ?? p.value) || p.key}>
                    {p.name ?? p.label ?? p.text ?? p.value ?? p.id}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="__none__" disabled>No partners available</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
        <TextField
          placeholder="Search tickets..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: '200px' }}
          disabled={loading}
        />
        {!hideFilters && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: '150px' }} disabled={loading}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Completed Tickets</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      
      {/* Table Container */}
      <TableContainer component={Paper} sx={{ ...noScrollBarStyle, flexGrow: 1, overflow: 'auto' }}>
        <Table stickyHeader aria-label="ticket table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ backgroundColor: '#064d51', color: 'white' }}>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredData.length}
                  checked={filteredData.length > 0 && selected.length === filteredData.length}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'select all tickets' }}
                  disabled={loading || filteredData.length === 0}
                  sx={{ color: 'white' }}
                />
              </TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Ticket Number</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Ticket Date</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Ticket Type</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Customer Name</TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Partner Payment</TableCell>
              <TableCell sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>Processed Date</TableCell>
              <TableCell align="center" sx={{ backgroundColor: '#064d51', color: 'white', fontWeight: 600 }}>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography variant="body2">Loading unpaid tickets...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              // Process tickets to handle workflow tickets with child tickets differently
              paginatedData.flatMap((ticket) => {
                const isWorkflowTicket = ticket.isWorkflow || ticket.ticketType === 'Workflow';
                const hasChildTickets = ticket.hasChildTickets && Array.isArray(ticket.childTickets) && ticket.childTickets.length > 0;
                
                // For workflow tickets with child tickets, only render the children
                if (isWorkflowTicket && hasChildTickets) {
                  return ticket.childTickets.map((childTicket) => (
                    <TableRow
                      hover
                      key={childTicket.id}
                      onClick={(event) => handleClick(event, childTicket.id)}
                      role="checkbox"
                      aria-checked={isSelected(childTicket.id)}
                      tabIndex={-1}
                      selected={isSelected(childTicket.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected(childTicket.id)}
                          inputProps={{ 'aria-labelledby': `ticket-${childTicket.id}` }}
                        />
                      </TableCell>
                      <TableCell component="th" id={`ticket-${childTicket.id}`} scope="row">
                        {childTicket.orgTicketNumber || '\u2014'}
                      </TableCell>
                      <TableCell>{childTicket.serviceName || '\u2014'}</TableCell>
                      <TableCell align="left">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: showChannelPartnerCommission ? 'warning.main' : 'info.main' }}>
                            {showChannelPartnerCommission ? formatCurrency(childTicket.channelPartnerCommission || 0) : formatCurrency(childTicket.partnerPayment || 0)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {showChannelPartnerCommission ? `Partner: ${formatCurrency(childTicket.partnerPayment || 0)}` : `Customer: ${formatCurrency(childTicket.totalCost)}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: 
                              childTicket.status === 'Completed' ? 'rgba(0, 128, 0, 0.1)' : 
                              'rgba(128, 128, 128, 0.1)',
                            color: 
                              childTicket.status === 'Completed' ? 'green' : 
                              'grey',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                          }}
                        >
                          {childTicket.status || '\u2014'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ));
                }
                
                // For standalone tickets or non-workflow tickets, render normally
                const isExpanded = expandedRows.has(ticket.ticketId);

                return [
                  // Main row
                  <TableRow
                    hover
                    key={ticket.ticketId}
                    role="checkbox"
                    aria-checked={isSelected(ticket.ticketId)}
                    tabIndex={-1}
                    selected={isSelected(ticket.ticketId)}
                  >
                    <TableCell padding="checkbox" onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      <Checkbox
                        checked={isSelected(ticket.ticketId)}
                        inputProps={{ 'aria-labelledby': `ticket-${ticket.ticketId}` }}
                      />
                    </TableCell>
                    {/* Ticket Number */}
                    <TableCell component="th" id={`ticket-${ticket.ticketId}`} scope="row" onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {ticket.ticketNumber || '\u2014'}
                    </TableCell>
                    {/* Ticket Date */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString() : '\u2014'}
                    </TableCell>
                    {/* Title */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {ticket.description || '\u2014'}
                    </TableCell>
                    {/* Ticket Type */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {ticket.serviceName || '\u2014'}
                    </TableCell>
                    {/* Customer Name */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {'\u2014'}
                    </TableCell>
                    {/* Amount */}
                    <TableCell align="right" onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {formatCurrency(ticket.totalCost || 0)}
                      </Typography>
                    </TableCell>
                    {/* Partner Payment Status - tickets on this page are always unpaid to partner */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: 'rgba(255, 165, 0, 0.1)',
                          color: 'warning.main',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                        }}
                      >
                        PENDING
                      </Box>
                    </TableCell>
                    {/* Processed Date */}
                    <TableCell onClick={(event) => handleClick(event, ticket.ticketId)} sx={{ cursor: 'pointer' }}>
                      {'\u2014'}
                    </TableCell>
                    {/* View Payment Detail */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPaymentDetail(ticket);
                        }}
                        sx={{
                          color: '#064d51',
                          '&:hover': {
                            backgroundColor: 'rgba(6, 77, 81, 0.1)'
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ];
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm || statusFilter !== 'all' ?
                      'No matching tickets found. Try adjusting your filters.' :
                      'No unpaid tickets found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination controls — suppressed when the parent drives pagination
          server-side (e.g., Service Partner Payments page) to avoid showing
          two competing "Rows per page" controls. */}
      {!hidePagination && (
      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        sx={{ mt: 2 }}
      />
      )}

      {/* Footer with summary information */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredData.length} {filteredData.length === 1 ? 'ticket' : 'tickets'} found
        </Typography>
        <Typography variant="body2">
          {selected.length > 0 ? `${selected.length} selected` : ''}
        </Typography>
      </Box>

      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#064d51', fontSize: '0.9rem', py: 1.25, px: 2 }}>
          Partner Payment
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 2 }}>
          {/* Amount */}
          <Box sx={{ mb: 1.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Total Amount</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#064d51' }}>
              {formatCurrency(totalSelected)}
            </Typography>
          </Box>

          {/* Payment Method — inline radio */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <FormControlLabel
              control={<Radio size="small" checked={paymentMethod === 'OFFLINE'} onChange={() => setPaymentMethod('OFFLINE')} />}
              label={<Typography sx={{ fontSize: '0.75rem' }}>Bank Transfer</Typography>}
              sx={{ flex: 1, m: 0, p: 0.5, border: paymentMethod === 'OFFLINE' ? '1.5px solid #064d51' : '1px solid #e0e0e0', borderRadius: 1, bgcolor: paymentMethod === 'OFFLINE' ? 'rgba(6,77,81,0.04)' : 'transparent' }}
            />
            <FormControlLabel
              control={<Radio size="small" checked={paymentMethod === 'ONLINE'} disabled />}
              label={<Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>Online (soon)</Typography>}
              sx={{ flex: 1, m: 0, p: 0.5, border: '1px solid #e0e0e0', borderRadius: 1 }}
            />
          </Box>

          {/* Source Bank (Thaklis) — merged label + select */}
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Source Bank (Thaklis)</InputLabel>
            <Select
              value={selectedBankAccount || ''}
              onChange={(e) => setSelectedBankAccount(e.target.value)}
              label="Source Bank (Thaklis)"
              sx={{ '& .MuiSelect-select': { py: 1 } }}
            >
              {bankAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id} disabled={account.availableBalance < totalSelected}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{account.bankName}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                      {account.accountNumber} · Balance: {formatCurrency(account.balance || 0)}
                      {account.pendingCommitted > 0 && (
                        <> · Processing: {formatCurrency(account.pendingCommitted)}</>
                      )}
                    </Typography>
                    {account.pendingCommitted > 0 && (
                      <Typography sx={{ fontSize: '0.6rem', color: account.availableBalance < totalSelected ? 'error.main' : 'success.main', fontWeight: 600 }}>
                        Available: {formatCurrency(account.availableBalance)}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
              {bankAccounts.length === 0 && <MenuItem disabled>No bank accounts</MenuItem>}
            </Select>
          </FormControl>

          {/* Destination Bank (Partner) — merged label + select */}
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Partner Bank Account</InputLabel>
            <Select
              value={selectedPartnerBank || ''}
              onChange={(e) => setSelectedPartnerBank(e.target.value)}
              label="Partner Bank Account"
              sx={{ '& .MuiSelect-select': { py: 1 } }}
            >
              {partnerBankAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{account.bankName}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                      {account.accountNumber} {account.accountHolderName && `(${account.accountHolderName})`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              {partnerBankAccounts.length === 0 && <MenuItem disabled>No partner bank accounts</MenuItem>}
            </Select>
          </FormControl>

          {/* Info */}
          <Box sx={{ p: 1, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#1565c0' }}>
              Payment will be transferred from Thaklis bank to partner's bank account.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowPaymentDialog(false)}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#9e9e9e', '&:hover': { bgcolor: '#757575' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPayment}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#064d51',
              color: '#ffffff',
              '&:hover': { bgcolor: '#043a3e' },
              '&:disabled': {
                bgcolor: '#d0d0d0',
                color: '#909090'
              }
            }}
            disabled={!paymentMethod || !selectedBankAccount || !selectedPartnerBank}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Processing Status Dialog */}
      <Dialog
        open={processingStatus !== null}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogContent sx={{ py: 4, px: 3, textAlign: 'center', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>

          {processingStatus === 'processing' && (
            <>
              <Box sx={{ mb: 2 }}>
                <CircularProgress size={60} sx={{ color: '#064d51' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Processing Payment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 2 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out 0.2s infinite' }} />
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#064d51', animation: 'pulse 1.5s ease-in-out 0.4s infinite' }} />
              </Box>
            </>
          )}

          {processingStatus === 'success' && (
            <>
              <Box sx={{ mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 70, color: '#4caf50' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Payment Successful
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                  Amount Paid: {formatCurrency(totalSelected)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                The payment has been processed and will be reflected shortly.
              </Typography>
            </>
          )}

          {processingStatus === 'error' && (
            <>
              <Box sx={{ mb: 2 }}>
                <ErrorIcon sx={{ fontSize: 70, color: '#d32f2f' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#064d51' }}>
                Payment Failed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {processingMessage}
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #d32f2f', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 500 }}>
                  Please try again or contact support for assistance.
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#064d51',
                  '&:hover': { bgcolor: '#043a3e' }
                }}
                onClick={() => {
                  setProcessingStatus(null);
                  setProcessingMessage('');
                }}
              >
                Try Again
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Detail Dialog */}
      <Dialog
        open={showPaymentDetailDialog}
        onClose={() => setShowPaymentDetailDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#064d51', borderBottom: '1px solid #e0e0e0', py: 1.5, px: 2, fontSize: '1rem' }}>
          Payment Breakdown
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 2 }}>
          {selectedTicketDetail && (
            <Box>
              {/* Ticket Information */}
              <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Ticket #</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {selectedTicketDetail.ticketNumber || selectedTicketDetail.orgTicketNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Service</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {selectedTicketDetail.serviceName}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Financial Breakdown */}
              <Box>
                {/* Service Charge */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Service Charge</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Base fee</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {formatCurrency(selectedTicketDetail.serviceCharge)}
                  </Typography>
                </Box>

                {/* Partner Service Fee (60% of Service Charge) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Partner Service Fee</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>60% of service charge</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#064d51', fontSize: '0.875rem' }}>
                    {formatCurrency(selectedTicketDetail.partnerServiceFee)}
                  </Typography>
                </Box>

                {/* Government Fees */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Government Fees</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Official charges</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {formatCurrency(selectedTicketDetail.governmentFees)}
                  </Typography>
                </Box>

                {/* Total Partner Payment */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  bgcolor: '#064d51',
                  borderRadius: 1
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'white', fontSize: '0.85rem' }}>
                      Total Payment
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                      To service partner
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>
                    {formatCurrency(selectedTicketDetail.partnerPayment)}
                  </Typography>
                </Box>
              </Box>

              {/* Info Note */}
              <Box sx={{ mt: 1.5, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500, fontSize: '0.7rem' }}>
                  Partner payment includes government fees (paid to government on behalf of customer) plus service charge
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, px: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setShowPaymentDetailDialog(false)}
            variant="contained"
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#064d51',
              fontSize: '0.8rem',
              '&:hover': { bgcolor: '#043a3e' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </Card>
  );
};

export default TicketMapping;
